import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { SLICE_NAMES } from '@/constants';
import { ChatAIMode } from '@/models/chatai/ChatAIMode';
import { IConversationListItem } from '@/models/chatai/IConversationListItem';
import { IConversationMessage } from '@/models/chatai/IConversationMessage';

export interface IChatAIHistoryState {
    items: IConversationListItem[];
    listTotal: number;
    listLoading: 'idle' | 'pending' | 'reloading';
    listError: string | null;

    /** uuid текущего открытого треда; null = ещё не выбран. */
    currentConversationId: string | null;
    /** true — тред уже создан на бэке (получен ответ); false — pending lazy-create. */
    isCurrentPersisted: boolean;
    /**
     * Timestamp markCurrentPersisted. Защищает active thread от удаления через applyListDelta
     * только в окне OPTIMISTIC_PROTECT_MS — иначе race с EXISTS-фильтром backend (qa_log
     * ещё не committed) удалил бы только что добавленный optimistic insert. После того, как
     * incoming уже содержит этот тред, флаг сбрасывается, и обычное удаление (включая
     * soft-delete из другого таба) опять работает.
     */
    optimisticInsertAt: number | null;

    /** Сообщения по conversationId. Локальные правки оптимистичные, потом подтверждаются с бэка. */
    messagesByConversation: Record<string, IConversationMessage[]>;
    messagesLoaded: Record<string, boolean>;
    messagesLoading: Record<string, boolean>;

    /** Mobile drawer. Десктоп — sidebar всегда виден. */
    sidebarOpenMobile: boolean;

    /**
     * Режим текущего треда. Для нового draft-треда (без сообщений) — выбирается юзером
     * через ChatAIModeSelector; persisted-тред lock'ает selector и забирает mode из БД.
     * Default 'rag' (или из localStorage('chat-ai.mode')).
     */
    currentMode: ChatAIMode;
}

/** Окно защиты optimistic-insert от applyListDelta. После backend записывает qa_log за < 1 сек,
 *  но даём запас на сетевую задержку push'а до клиента. */
const OPTIMISTIC_PROTECT_MS = 10_000;

const PREFERRED_MODE_STORAGE_KEY = 'chat-ai.mode';

function readPreferredMode(): ChatAIMode {
    if (typeof window === 'undefined') return 'rag';
    try {
        const stored = window.localStorage.getItem(PREFERRED_MODE_STORAGE_KEY);
        return stored === 'agent' ? 'agent' : 'rag';
    } catch {
        return 'rag';
    }
}

function persistPreferredMode(mode: ChatAIMode): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(PREFERRED_MODE_STORAGE_KEY, mode);
    } catch {
        /* localStorage недоступен — игнорируем, default восстановит при следующей загрузке */
    }
}

const initialState: IChatAIHistoryState = {
    items: [],
    listTotal: 0,
    listLoading: 'idle',
    listError: null,

    currentConversationId: null,
    isCurrentPersisted: false,
    optimisticInsertAt: null,

    messagesByConversation: {},
    messagesLoaded: {},
    messagesLoading: {},

    sidebarOpenMobile: false,
    currentMode: readPreferredMode(),
};

export const ChatAIHistorySlice = createSlice({
    name: SLICE_NAMES.CHAT_AI_HISTORY,
    initialState,
    reducers: {
        // ---------- Список тредов ----------
        listLoadStart: (state, action: PayloadAction<{ reset?: boolean }>) => {
            state.listLoading = action.payload.reset ? 'reloading' : 'pending';
            state.listError = null;
        },
        listLoadSuccess: (
            state,
            action: PayloadAction<{ items: IConversationListItem[]; total: number; reset: boolean }>,
        ) => {
            const { items, total, reset } = action.payload;
            if (reset) {
                // Защита active только в окне optimistic-insert: иначе если этот же тред
                // soft-deleted в другом табе или зачищен retention'ом, applyListDelta его
                // никогда не удалит и UI будет работать со «сталым» тредом.
                const cur = state.currentConversationId;
                const inOptimisticWindow = state.optimisticInsertAt != null
                    && Date.now() - state.optimisticInsertAt < OPTIMISTIC_PROTECT_MS;
                const protectedId = inOptimisticWindow ? cur : null;
                applyListDelta(state.items, items, protectedId);

                // Закрываем optimistic-окно: либо incoming уже содержит cur (race
                // на EXISTS-фильтр закрылась), либо мы вышли за таймаут.
                if (state.optimisticInsertAt != null) {
                    const present = cur != null && items.some((i) => i.conversationId === cur);
                    if (!inOptimisticWindow || present) {
                        state.optimisticInsertAt = null;
                    }
                }

                // Active persisted тред пропал из листинга → удалили в другом табе или retention.
                // Сбрасываем active, чтобы форма (useEffect[sliceCurrentId]) ушла на hello.
                if (
                    cur != null
                    && state.isCurrentPersisted
                    && !state.items.some((i) => i.conversationId === cur)
                ) {
                    state.currentConversationId = null;
                    state.isCurrentPersisted = false;
                    delete state.messagesByConversation[cur];
                    delete state.messagesLoaded[cur];
                    delete state.messagesLoading[cur];
                }
            } else {
                state.items = mergeUnique(state.items, items);
            }
            state.listTotal = total;
            state.listLoading = 'idle';
        },
        listLoadError: (state, action: PayloadAction<string>) => {
            state.listLoading = 'idle';
            state.listError = action.payload;
        },

        // ---------- Активный тред ----------
        setActiveConversation: (state, action: PayloadAction<string | null>) => {
            if (action.payload == null) {
                state.currentConversationId = null;
                state.isCurrentPersisted = false;
                return;
            }
            const item = state.items.find((i) => i.conversationId === action.payload);
            if (!item) {
                // Race: тред удалён в другом табе между snapshot'ом sidebar и кликом юзера.
                // Не выставляем currentConversationId — иначе currentMode осталось бы от
                // предыдущего треда, и форма работала бы со «сталым» mode без persisted-треда
                // в БД. handleSelect в sidebar уже делает аналогичный guard через itemsRef.
                return;
            }
            state.currentConversationId = action.payload;
            state.isCurrentPersisted = true;
            // Mode persisted-треда — source of truth, перетирает текущий выбор юзера.
            // Selector в форме под этим тредом lock'ается до создания нового треда.
            state.currentMode = item.mode;
        },
        /**
         * Смена режима для draft-треда. Допустим только при пустом не-persisted треде —
         * иначе тред уже отправлен в БД с фиксированным mode (см. EnsureConversationAsync
         * на бэке: ModeMismatch при попытке записать в чужой mode).
         */
        setMode: (state, action: PayloadAction<ChatAIMode>) => {
            if (state.currentMode === action.payload) return;
            state.currentMode = action.payload;
            persistPreferredMode(action.payload);
        },
        startNewConversation: (state) => {
            // idempotent: если current ещё не persisted и messages пустые — оставляем как есть.
            const cur = state.currentConversationId;
            if (cur && !state.isCurrentPersisted) {
                const msgs = state.messagesByConversation[cur];
                if (!msgs || msgs.length === 0) return;
            }
            const id = uuidv4();
            state.currentConversationId = id;
            state.isCurrentPersisted = false;
            state.messagesByConversation[id] = [];
            state.messagesLoaded[id] = true;
        },
        markCurrentPersisted: (
            state,
            action: PayloadAction<{ title: string } | undefined>,
        ) => {
            // Идемпотентно: вызывается на каждом ответе, но реальная работа — только в момент
            // first-persist (false → true). Иначе любое сообщение после первого триггерило бы
            // лишний refetch через useEffect[isCurrentPersisted] в sidebar.
            if (state.isCurrentPersisted) return;
            state.isCurrentPersisted = true;
            state.optimisticInsertAt = Date.now();
            // Optimistic insert: при первом ответе сразу добавляем активный тред в items,
            // не дожидаясь SignalR conversationsUpdated → refetch (там 300ms debounce + lag).
            // Если SignalR-refetch потом отдаст «настоящий» элемент — applyListDelta заменит ссылку.
            const cur = state.currentConversationId;
            if (cur && !state.items.some((i) => i.conversationId === cur)) {
                const now = new Date().toISOString();
                const title = (action?.payload?.title ?? '').slice(0, 60).trim() || 'Новый чат';
                state.items.unshift({
                    conversationId: cur,
                    title,
                    createdAt: now,
                    updatedAt: now,
                    isPending: false,
                    mode: state.currentMode,
                });
            }
        },

        // ---------- Сообщения треда ----------
        messagesLoadStart: (state, action: PayloadAction<string>) => {
            state.messagesLoading[action.payload] = true;
        },
        messagesLoadSuccess: (
            state,
            action: PayloadAction<{ conversationId: string; messages: IConversationMessage[] }>,
        ) => {
            const { conversationId, messages } = action.payload;
            state.messagesByConversation[conversationId] = messages;
            state.messagesLoaded[conversationId] = true;
            state.messagesLoading[conversationId] = false;
        },
        messagesLoadError: (state, action: PayloadAction<string>) => {
            state.messagesLoading[action.payload] = false;
        },
        addLocalMessage: (
            state,
            action: PayloadAction<{ conversationId: string; message: IConversationMessage }>,
        ) => {
            const { conversationId, message } = action.payload;
            const list = state.messagesByConversation[conversationId] ?? [];
            state.messagesByConversation[conversationId] = [...list, message];
        },
        updateLocalMessage: (
            state,
            action: PayloadAction<{ conversationId: string; messageId: number | string; patch: Partial<IConversationMessage> }>,
        ) => {
            const { conversationId, messageId, patch } = action.payload;
            const list = state.messagesByConversation[conversationId];
            if (!list) return;
            state.messagesByConversation[conversationId] = list.map((m) =>
                m.id === messageId || m.requestId === messageId ? { ...m, ...patch } : m,
            );
        },
        // ---------- Mutations списка ----------
        renameLocally: (state, action: PayloadAction<{ conversationId: string; title: string }>) => {
            const item = state.items.find((i) => i.conversationId === action.payload.conversationId);
            if (item) item.title = action.payload.title;
        },
        removeLocally: (state, action: PayloadAction<string>) => {
            state.items = state.items.filter((i) => i.conversationId !== action.payload);
            if (state.currentConversationId === action.payload) {
                state.currentConversationId = null;
                state.isCurrentPersisted = false;
            }
            delete state.messagesByConversation[action.payload];
            delete state.messagesLoaded[action.payload];
            delete state.messagesLoading[action.payload];
        },

        // ---------- Mobile sidebar ----------
        toggleSidebarMobile: (state, action: PayloadAction<boolean | undefined>) => {
            state.sidebarOpenMobile = action.payload !== undefined ? action.payload : !state.sidebarOpenMobile;
        },

        // ---------- Полный сброс (logout / unmount widget) ----------
        resetHistory: () => initialState,
    },
});

function mergeUnique(prev: IConversationListItem[], next: IConversationListItem[]): IConversationListItem[] {
    const seen = new Set(prev.map((i) => i.conversationId));
    return prev.concat(next.filter((i) => !seen.has(i.conversationId)));
}

/**
 * Дельта-merge при reset=true: вместо замены всего массива
 * (а) удаляем item'ы, которых нет в incoming;
 * (б) обновляем поля для существующих (только реально изменённые — Immer не "пометит" неизменившиеся);
 * (в) добавляем новые;
 * (г) переставляем порядок согласно incoming.
 *
 * Это сохраняет идентичность объектов в state, и React не пересоздаёт DOM-ноды
 * для строк, у которых поля не поменялись (нет мерцаний, scroll стабилен).
 *
 * Mutation работает благодаря Immer (RTK).
 */
function applyListDelta(
    current: IConversationListItem[],
    incoming: IConversationListItem[],
    protectedId: string | null,
): void {
    const incomingIds = new Set(incoming.map((i) => i.conversationId));

    // (а) удалить отсутствующие — обходим с конца, чтобы splice не сбивал индексы.
    // protectedId передаётся ТОЛЬКО в окне optimistic-insert (см. listLoadSuccess):
    // backend мог не успеть записать qa_log → EXISTS-фильтр исключит наш только что
    // созданный тред — это race, не реальное удаление. Вне этого окна protectedId=null,
    // и applyListDelta удаляет любой active, который пропал из листинга (soft-delete
    // в другом табе, retention).
    for (let i = current.length - 1; i >= 0; i--) {
        const id = current[i].conversationId;
        if (!incomingIds.has(id) && id !== protectedId) {
            current.splice(i, 1);
        }
    }

    // (б) обновить поля у существующих, (в) добавить недостающие.
    const byId = new Map<string, IConversationListItem>();
    for (const item of current) byId.set(item.conversationId, item);

    for (const next of incoming) {
        const cur = byId.get(next.conversationId);
        if (cur) {
            if (cur.title !== next.title) cur.title = next.title;
            if (cur.createdAt !== next.createdAt) cur.createdAt = next.createdAt;
            if (cur.updatedAt !== next.updatedAt) cur.updatedAt = next.updatedAt;
            if (cur.isPending !== next.isPending) cur.isPending = next.isPending;
            // mode у persisted треда не меняется (CHECK constraint на mode + INSERT-only),
            // но синхронизируем на случай ручной правки в БД и для optimistic-insert,
            // где cur.mode ставился по state.currentMode на момент insert'а — backend
            // отдаёт канон.
            if (cur.mode !== next.mode) cur.mode = next.mode;
        } else {
            current.push({ ...next });
            byId.set(next.conversationId, current[current.length - 1]);
        }
    }

    // (г) reorder по incoming — incoming уже отсортирован по updatedAt DESC от бэка.
    // protectedId сохраняет свой порядок (как был — обычно сверху, потому что optimistic insert через unshift).
    const orderById = new Map<string, number>();
    incoming.forEach((it, idx) => orderById.set(it.conversationId, idx));
    current.sort(
        (a, b) =>
            (orderById.get(a.conversationId) ?? Number.MAX_SAFE_INTEGER) -
            (orderById.get(b.conversationId) ?? Number.MAX_SAFE_INTEGER),
    );
}

export const {
    listLoadStart,
    listLoadSuccess,
    listLoadError,
    setActiveConversation,
    setMode,
    startNewConversation,
    markCurrentPersisted,
    messagesLoadStart,
    messagesLoadSuccess,
    messagesLoadError,
    addLocalMessage,
    updateLocalMessage,
    renameLocally,
    removeLocally,
    toggleSidebarMobile,
    resetHistory,
} = ChatAIHistorySlice.actions;

export default ChatAIHistorySlice.reducer;
